import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import PeopleIcon from '@mui/icons-material/People';
import { getPublicVacancyById } from '../../modules/apps/user-management/users-list/core/_requests';

const PublicVacancyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vacancy, setVacancy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVacancy();
  }, [id]);

  const loadVacancy = async () => {
    try {
      setLoading(true);
      const response = await getPublicVacancyById(id!);
      if (response.success) {
        setVacancy(response.data);
      } else {
        setError('Vacante no encontrada');
      }
    } catch (error) {
      console.error('Error loading vacancy:', error);
      setError('Error al cargar la vacante');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Cargando vacante...</Typography>
      </Container>
    );
  }

  if (error || !vacancy) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error">{error || 'Vacante no encontrada'}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/vacantes')}>
          Volver a vacantes
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/vacantes" onClick={(e) => { e.preventDefault(); navigate('/vacantes'); }}>
          Vacantes
        </Link>
        <Typography color="text.primary">{vacancy.nombre_vacante}</Typography>
      </Breadcrumbs>

      {/* Botón volver */}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/vacantes')} sx={{ mb: 3 }}>
        Volver a vacantes
      </Button>

      <Grid container spacing={4}>
        {/* Información principal */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {/* Código y título */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Chip label={`Código: ${vacancy.codigo_vacante || 'Sin código'}`} color="primary" />
                <Chip label={vacancy.modalidad?.label || 'Modalidad no especificada'} variant="outlined" />
              </Box>

              <Typography variant="h4" gutterBottom>
                {vacancy.nombre_vacante}
              </Typography>

              {/* Empresa */}
              <Box display="flex" alignItems="center" mb={3}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary">
                  {vacancy.empresa?.nombre || 'Empresa no especificada'}
                </Typography>
              </Box>

              {/* Número de puestos */}
              {vacancy.numero_puestos && (
                <Box display="flex" alignItems="center" mb={2}>
                  <PeopleIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Número de vacantes:</strong> {vacancy.numero_puestos}
                  </Typography>
                </Box>
              )}

              {/* Descripción */}
              <Typography variant="h6" gutterBottom mt={3}>
                Descripción de la vacante
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                {vacancy.descripcion}
              </Typography>

              {/* Cargos */}
              {vacancy.cargos && vacancy.cargos.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom mt={4}>
                    Cargos disponibles
                  </Typography>
                  {vacancy.cargos.map((cargo: any) => (
                    <Chip
                      key={cargo.id}
                      label={`${cargo.nombre} (${cargo.codigo_cuoc})`}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </>
              )}

              {/* Requisitos */}
              {vacancy.requisitos?.formacion_titulo && (
                <>
                  <Typography variant="h6" gutterBottom mt={4}>
                    Requisitos de formación
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                    {vacancy.requisitos.formacion_titulo}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Información lateral */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de la vacante
              </Typography>

              <List>
                {/* Salario */}
                <ListItem>
                  <ListItemIcon>
                    <MonetizationOnIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Salario"
                    secondary={
                      vacancy.salario?.minimo_formateado
                        ? `${vacancy.salario.minimo_formateado}${vacancy.salario.maximo ? ` - ${vacancy.salario.maximo_formateado}` : ''}`
                        : 'A convenir'
                    }
                  />
                </ListItem>

                <Divider />

                {/* Tipo de contrato */}
                <ListItem>
                  <ListItemIcon>
                    <WorkIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Tipo de contrato"
                    secondary={vacancy.tipo_contrato?.label || 'No especificado'}
                  />
                </ListItem>

                <Divider />

                {/* Nivel de estudio */}
                <ListItem>
                  <ListItemIcon>
                    <SchoolIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Nivel de estudio"
                    secondary={vacancy.nivel_educativo?.label || 'No especificado'}
                  />
                </ListItem>

                <Divider />

                {/* Experiencia */}
                <ListItem>
                  <ListItemIcon>
                    <AccessTimeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Experiencia requerida"
                    secondary={vacancy.experiencia?.label || 'No especificada'}
                  />
                </ListItem>

                <Divider />

                {/* Jornada laboral */}
                <ListItem>
                  <ListItemIcon>
                    <AccessTimeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Jornada laboral"
                    secondary={vacancy.jornada_laboral?.label || 'No especificada'}
                  />
                </ListItem>

                <Divider />

                {/* Ubicación */}
                <ListItem>
                  <ListItemIcon>
                    <LocationOnIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Ubicación"
                    secondary={vacancy.ubicacion?.ubicacion_completa || 'No especificada'}
                  />
                </ListItem>

                {/* Fecha límite */}
                {vacancy.fecha_limite_aplicacion && (
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Fecha límite de aplicación"
                        secondary={vacancy.fecha_limite_aplicacion.split('-').reverse().join('/')} />
                    </ListItem>
                  </>
                )}
              </List>

              {/* Información de la empresa */}
              {!vacancy.es_confidencial && vacancy.empresa ? (
                <>
                  <Typography variant="h6" gutterBottom mt={3}>
                    Información de la empresa
                  </Typography>

                  <List>
                    {/* Descripción empresa */}
                    {vacancy.empresa.descripcion && (
                      <ListItem>
                        <ListItemText
                          primary="Descripción"
                          secondary={vacancy.empresa.descripcion}
                        />
                      </ListItem>
                    )}

                    {/* CIIU */}
                    {vacancy.empresa.codigo_ciiu && (
                      <ListItem>
                        <ListItemText
                          primary="Código CIIU"
                          secondary={vacancy.empresa.codigo_ciiu}
                        />
                      </ListItem>
                    )}

                    {/* Contacto */}
                    {vacancy.empresa.telefono && (
                      <ListItem>
                        <ListItemIcon>
                          <PhoneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Teléfono"
                          secondary={vacancy.empresa.telefono}
                        />
                      </ListItem>
                    )}
                  </List>
                </>
              ) : vacancy.es_confidencial ? (
                <Box mt={3} p={2} bgcolor="warning.light" borderRadius={1}>
                  <Typography variant="body2" color="warning.contrastText">
                    <strong>Información confidencial:</strong> Esta vacante ha sido marcada como confidencial.
                    La información de la empresa no es pública.
                  </Typography>
                </Box>
              ) : null}

              {/* Botón postular */}
              <Box mt={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => window.open(window.location.origin, '_blank')}
                >
                  Postular a esta vacante
                </Button>
                <Typography variant="caption" display="block" textAlign="center" mt={1}>
                  Serás redirigido a la plataforma VCC para completar tu registro
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fecha de publicación */}
      <Box mt={4} textAlign="center">
        <Typography variant="caption" color="text.secondary">
          Vacante publicada el {new Date(vacancy.fecha_publicacion).toLocaleDateString('es-CO')}
        </Typography>
      </Box>
    </Container>
  );
};

export default PublicVacancyDetailPage;